import { useState, useRef, useCallback, useEffect, type JSX, type ReactNode } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  FolderOpen,
  Package,
  ShoppingCart,
  Boxes,
  Tag,
  Truck,
  RotateCcw,
  ArrowLeftRight,
  DollarSign,
  BarChart2,
  Building2,
  Settings,
  ChevronDown,
  User,
  HelpCircle,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';
import { useHelpOverlay } from '../../hooks/useHelpOverlay';
import { supabase } from '../../lib/supabase';
import { canAccess } from '../../lib/roles';

// ── Nav definition ────────────────────────────────────────────────────────────

interface NavChild {
  label: string;
  to: string;
  badge?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string | null;
  type: 'single' | 'expand' | 'panel';
  badge?: boolean;
  children?: NavChild[];
}

const NAV: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard', type: 'single' },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare, to: '/tasks', type: 'single', badge: true },
  { id: 'files', label: 'Files', icon: FolderOpen, to: '/files', type: 'single' },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: Package,
    to: null,
    type: 'expand',
    children: [
      { label: 'Products', to: '/catalog/products' },
      { label: 'Flagged', to: '/catalog/flagged', badge: true },
      { label: 'Intake', to: '/catalog/intake' },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    icon: ShoppingCart,
    to: '/purchasing',
    type: 'expand',
    children: [
      { label: 'Buying Session', to: '/purchasing/buying-session' },
      { label: 'PO Builder', to: '/purchasing/po-builder' },
      { label: 'Purchase Orders', to: '/purchasing/purchase-orders' },
      { label: 'Receiving', to: '/purchasing/receiving', badge: true },
      { label: 'Discrepancies', to: '/purchasing/discrepancies', badge: true },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Boxes,
    to: '/inventory',
    type: 'expand',
    children: [
      { label: 'Restock', to: '/inventory/restock' },
      { label: 'Reconciliation', to: '/inventory/reconciliation' },
    ],
  },
  {
    id: 'listings',
    label: 'Listings',
    icon: Tag,
    to: '/listings',
    type: 'expand',
    badge: true,
    children: [{ label: 'Review', to: '/listings/review' }],
  },
  {
    id: 'fulfillment',
    label: 'Fulfillment',
    icon: Truck,
    to: '/fulfillment',
    type: 'expand',
    children: [
      { label: 'Orders', to: '/fulfillment/orders', badge: true },
      { label: 'Channel Fulfilled', to: '/fulfillment/channel-fulfilled', badge: true },
      { label: 'Review', to: '/fulfillment/review', badge: true },
      { label: 'Verify', to: '/fulfillment/verify', badge: true },
      { label: 'Fulfill', to: '/fulfillment/fulfill', badge: true },
      { label: 'Pick', to: '/fulfillment/pick', badge: true },
      { label: 'Ship', to: '/fulfillment/ship', badge: true },
      { label: 'Awaiting Label', to: '/fulfillment/awaiting-label', badge: true },
      { label: 'Awaiting Pickup', to: '/fulfillment/awaiting-pickup', badge: true },
      { label: 'PTO', to: '/fulfillment/pto', badge: true },
      { label: 'On Hold', to: '/fulfillment/on-hold', badge: true },
      { label: 'Followups', to: '/fulfillment/followups', badge: true },
      { label: 'Discrepancies', to: '/fulfillment/discrepancies', badge: true },
    ],
  },
  {
    id: 'returns',
    label: 'Returns',
    icon: RotateCcw,
    to: '/returns',
    type: 'expand',
    children: [
      { label: 'Inspection', to: '/returns/inspection', badge: true },
      { label: 'Disposition', to: '/returns/disposition', badge: true },
      { label: 'Discrepancies', to: '/returns/discrepancies', badge: true },
    ],
  },
  {
    id: 'transfers',
    label: 'Transfers',
    icon: ArrowLeftRight,
    to: null,
    type: 'expand',
    children: [
      { label: 'Inbound', to: '/transfers/inbound', badge: true },
      { label: 'Removals', to: '/transfers/removals', badge: true },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign,
    to: null,
    type: 'expand',
    children: [
      { label: 'Credit & Cash', to: '/finance/credit-cash' },
      { label: 'Invoices', to: '/finance/invoices' },
      { label: 'Payouts', to: '/finance/payouts' },
      { label: 'Reconciliation', to: '/finance/reconciliation' },
      { label: 'Expenses', to: '/finance/expenses' },
      { label: 'Payroll', to: '/finance/payroll' },
      { label: 'Review', to: '/finance/review' },
    ],
  },
  { id: 'reports', label: 'Reports', icon: BarChart2, to: '/reports', type: 'panel' },
  {
    id: 'directory',
    label: 'Directory',
    icon: Building2,
    to: null,
    type: 'expand',
    children: [
      { label: 'Distributors', to: '/directory/distributors' },
      { label: 'Manufacturers', to: '/directory/manufacturers' },
      { label: 'Brands', to: '/directory/brands' },
      { label: 'Categories', to: '/directory/categories' },
      { label: 'Marketplaces', to: '/directory/marketplaces' },
      { label: 'Warehouses', to: '/directory/warehouses' },
    ],
  },
  { id: 'settings', label: 'Settings', icon: Settings, to: '/settings', type: 'panel' },
];

// Uniform icon size for all nav items
const ICON_SIZE = 18;

// ── Count badge — only shown when count > 0 ────────────────────────────────

interface CountBadgeProps {
  count?: number;
  pulse?: boolean;
  onAnimationEnd?: () => void;
}

function CountBadge({
  count = 0,
  pulse = false,
  onAnimationEnd,
}: CountBadgeProps): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        'ml-auto min-w-[1.25rem] h-[1.125rem] rounded-full bg-accent/20',
        'text-2xs text-accent font-mono flex items-center justify-center px-1 leading-none',
        pulse && 'pulse'
      )}
      onAnimationEnd={onAnimationEnd}
    >
      {/* KA1-F2 fold-in (DATA) — thousands-separated count (e.g. 2,432) */}
      {count.toLocaleString()}
    </span>
  );
}

// ── Tooltip — positioned to the right of collapsed sidebar icons ────────────

interface TooltipProps {
  text: string;
  children: ReactNode;
}

function Tooltip({ text, children }: TooltipProps): JSX.Element {
  const [show, setShow] = useState<boolean>(false);
  const [pos, setPos] = useState<{ top: number }>({ top: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleEnter = (): void => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.top + rect.height / 2 });
    }
    setShow(true);
  };

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      className="relative"
    >
      {children}
      {show && (
        <div
          className="fixed z-50 ml-2 px-2 py-1 rounded bg-surface-4 border border-border text-xs text-ink-bright
                     whitespace-nowrap pointer-events-none shadow-lg"
          style={{ left: 56, top: pos.top, transform: 'translateY(-50%)' }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

type FulfillmentCounts = Record<string, number | null>;

export default function Sidebar(): JSX.Element {
  const { sidebarCollapsed, user, signOut, density, role } = useApp();
  const { open: openHelp, helpButtonRef } = useHelpOverlay();
  const location = useLocation();
  const navigate = useNavigate();

  const collapsed = sidebarCollapsed;
  const py = density === 'comfortable' ? 'py-2' : 'py-1.5';

  // ── Live badge counts ──────────────────────────────────────
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

  // ── Pulse animation for badge count increases ─────────────
  const [pulsePaths, setPulsePaths] = useState<Record<string, boolean | undefined>>({});
  const prevBadgeCountsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const prev = prevBadgeCountsRef.current;
    const newPulses: Record<string, boolean> = {};
    for (const [path, count] of Object.entries(badgeCounts)) {
      if (count > (prev[path] || 0)) newPulses[path] = true;
    }
    if (Object.keys(newPulses).length > 0) {
      setPulsePaths((p) => ({ ...p, ...newPulses }));
      const t = setTimeout(() => {
        setPulsePaths((p) => {
          const next = { ...p };
          for (const path of Object.keys(newPulses)) delete next[path];
          return next;
        });
      }, 4600);
      prevBadgeCountsRef.current = { ...badgeCounts };
      return () => clearTimeout(t);
    }
    prevBadgeCountsRef.current = { ...badgeCounts };
  }, [badgeCounts]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('product_flags')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'open')
      .then(
        ({ count }) => {
          if (count !== null) setBadgeCounts((prev) => ({ ...prev, '/catalog/flagged': count }));
        },
        (e: Error) => console.error('[Sidebar] flagged count error:', e)
      );

    supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .in('status', ['open', 'in_progress'])
      .then(
        ({ count }) => {
          if (count !== null) setBadgeCounts((prev) => ({ ...prev, '/tasks': count }));
        },
        (e: Error) => console.error('[Sidebar] tasks count error:', e)
      );

    supabase
      .from('purchase_orders')
      .select('id', { count: 'exact', head: true })
      .in('status', ['invoice_received', 'partially_received'])
      .then(
        ({ count }) => {
          if (count !== null)
            setBadgeCounts((prev) => ({ ...prev, '/purchasing/receiving': count }));
        },
        (e: Error) => console.error('[Sidebar] receiving count error:', e)
      );

    supabase
      .from('receiving_line_items')
      .select('id', { count: 'exact', head: true })
      .eq('has_discrepancy', true)
      .then(
        async ({ count: total }) => {
          const { count: resolved } = await supabase
            .from('discrepancy_resolutions')
            .select('id', { count: 'exact', head: true });
          const unresolved = (total || 0) - (resolved || 0);
          if (unresolved >= 0)
            setBadgeCounts((prev) => ({
              ...prev,
              '/purchasing/discrepancies': unresolved,
            }));
        },
        (e: Error) => console.error('[Sidebar] discrepancies count error:', e)
      );

    supabase
      .from('listing_links')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'suppressed')
      .eq('is_active', true)
      .then(
        ({ count }) => {
          if (count !== null) setBadgeCounts((prev) => ({ ...prev, '/listings': count }));
        },
        (e: Error) => console.error('[Sidebar] listings count error:', e)
      );

    supabase
      .from('returns')
      .select('id', { count: 'exact', head: true })
      .in('status', ['initiated', 'in_transit', 'received', 'inspection'])
      .then(
        ({ count }) => {
          if (count) setBadgeCounts((prev) => ({ ...prev, '/returns/inspection': count }));
        },
        (e: Error) => console.error('[Sidebar] returns inspection error:', e)
      );
    supabase
      .from('returns')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'inspection')
      .is('disposition', null)
      .then(
        ({ count }) => {
          if (count) setBadgeCounts((prev) => ({ ...prev, '/returns/disposition': count }));
        },
        (e: Error) => console.error('[Sidebar] returns disposition error:', e)
      );
    supabase
      .from('returns')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'discrepancy')
      .then(
        ({ count }) => {
          if (count) setBadgeCounts((prev) => ({ ...prev, '/returns/discrepancies': count }));
        },
        (e: Error) => console.error('[Sidebar] returns discrepancies error:', e)
      );

    supabase
      .from('transfers')
      .select('id', { count: 'exact', head: true })
      .eq('transfer_type', 'inbound')
      .in('status', ['pending', 'in_transit'])
      .then(
        ({ count }) => {
          if (count) setBadgeCounts((prev) => ({ ...prev, '/transfers/inbound': count }));
        },
        (e: Error) => console.error('[Sidebar] transfers inbound error:', e)
      );
    supabase
      .from('transfers')
      .select('id', { count: 'exact', head: true })
      .eq('transfer_type', 'removal')
      .in('status', ['pending', 'in_transit'])
      .then(
        ({ count }) => {
          if (count) setBadgeCounts((prev) => ({ ...prev, '/transfers/removals': count }));
        },
        (e: Error) => console.error('[Sidebar] transfers removals error:', e)
      );

    const applyFulfillmentCounts = (data: FulfillmentCounts | null): void => {
      if (!data) return;
      const c = data;
      const totalActive = Object.entries(c)
        .filter(([k]) => !['delivered', 'channel_fulfilled', 'cancelled'].includes(k))
        .reduce((sum, [, v]) => sum + (v || 0), 0);
      setBadgeCounts((prev) => ({
        ...prev,
        '/fulfillment/orders': totalActive,
        '/fulfillment/channel-fulfilled': c.channel_fulfilled || 0,
        '/fulfillment/review': c.review || 0,
        '/fulfillment/verify': c.verified || 0,
        '/fulfillment/fulfill': c.fulfilling || 0,
        '/fulfillment/pick': c.picking || 0,
        '/fulfillment/ship': (c.packed || 0) + (c.awaiting_label || 0) + (c.awaiting_pickup || 0),
        '/fulfillment/awaiting-label': c.awaiting_label || 0,
        '/fulfillment/awaiting-pickup': c.awaiting_pickup || 0,
        '/fulfillment/pto': c.pto || 0,
        '/fulfillment/on-hold': c.on_hold || 0,
        '/fulfillment/followups': c.followup || 0,
        '/fulfillment/discrepancies': c.discrepancy || 0,
      }));
    };

    supabase.rpc('get_fulfillment_counts').then(
      ({ data }) => applyFulfillmentCounts(data as FulfillmentCounts | null),
      (e: Error) => console.error('[Sidebar] fulfillment counts error:', e)
    );

    const ffChannel = supabase
      .channel('fulfillment-badges')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'fulfillment_orders' }, () => {
        supabase.rpc('get_fulfillment_counts').then(
          ({ data }) => applyFulfillmentCounts(data as FulfillmentCounts | null),
          (e: Error) => console.error('[Sidebar] fulfillment realtime refresh error:', e)
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(ffChannel);
    };
  }, [user]);

  // ── Section open/close state ─────────────────────────────────
  const [manualState, setManualState] = useState<Record<string, boolean | undefined>>({});

  const isInDomain = useCallback(
    (item: NavItem): boolean => {
      const base = '/' + item.id;
      return location.pathname === base || location.pathname.startsWith(base + '/');
    },
    [location.pathname]
  );

  const isSectionOpen = useCallback(
    (item: NavItem): boolean => {
      const manual = manualState[item.id];
      if (manual === true) return true;
      if (manual === false) return false;
      return isInDomain(item);
    },
    [manualState, isInDomain]
  );

  const handleChevronClick = useCallback(
    (item: NavItem): void => {
      const currentlyOpen = isSectionOpen(item);
      setManualState((prev) => ({ ...prev, [item.id]: !currentlyOpen }));
    },
    [isSectionOpen]
  );

  const isPathActive = useCallback(
    (path: string | null): boolean => {
      if (!path) return false;
      if (path === '/dashboard')
        return location.pathname === '/dashboard' || location.pathname === '/';
      return location.pathname === path;
    },
    [location.pathname]
  );

  const isSectionActive = useCallback(
    (item: NavItem): boolean => {
      if (item.type === 'single') return isPathActive(item.to);
      if (item.type === 'panel') return item.to ? location.pathname.startsWith(item.to) : false;
      return isInDomain(item);
    },
    [isPathActive, isInDomain, location.pathname]
  );

  // Label click — navigate, and collapse if already in that section.
  // When the parent path is denied for this role (B12 pattern — dist_rep on
  // /purchasing can reach /purchasing/receiving but not the parent landing),
  // fall through to the first accessible child instead of firing a
  // ProtectedRoute redirect.
  const handleLabelClick = useCallback(
    (item: NavItem): void => {
      if (item.type === 'expand' && isInDomain(item) && isSectionOpen(item)) {
        setManualState((prev) => ({ ...prev, [item.id]: false }));
        return;
      }
      if (item.to && canAccess(role, item.to)) {
        navigate(item.to);
        return;
      }
      const firstAccessibleChild = item.children?.find((c) => canAccess(role, c.to));
      if (firstAccessibleChild) navigate(firstAccessibleChild.to);
    },
    [navigate, isInDomain, isSectionOpen, role]
  );

  // Filter nav items by role. A section (expand type) is visible if either
  // the parent path or any child path is accessible — child-level PAGE_ACCESS
  // grants lift the parent into view even when the parent itself is denied.
  // Leaf items (single / panel) resolve against their own `to`.
  const visibleNav = NAV.filter((item) => {
    if (!item.children || item.children.length === 0) {
      const checkPath = item.to || `/${item.id}`;
      return canAccess(role, checkPath);
    }
    const parentAccessible = item.to ? canAccess(role, item.to) : false;
    const anyChildAccessible = item.children.some((c) => canAccess(role, c.to));
    return parentAccessible || anyChildAccessible;
  });

  // ── Render helpers ───────────────────────────────────────────

  const renderCollapsedItem = (item: NavItem, active: boolean): JSX.Element => {
    const Icon = item.icon;
    const btn = (
      <button
        onClick={() => handleLabelClick(item)}
        className={clsx(
          'w-full flex items-center justify-center rounded-md cursor-pointer transition-colors duration-100',
          'h-9',
          active
            ? 'bg-accent-muted text-accent'
            : 'text-ink-secondary hover:bg-surface-4 hover:text-ink-primary'
        )}
        aria-label={item.label}
      >
        <Icon size={ICON_SIZE} />
      </button>
    );
    return <Tooltip text={item.label}>{btn}</Tooltip>;
  };

  return (
    <aside
      className={clsx(
        'flex flex-col h-full bg-sidebar transition-all duration-200 flex-shrink-0 overflow-hidden',
        collapsed ? 'w-[52px]' : 'w-[220px]'
      )}
    >
      {/* Scrollable nav — independent scroll, bottom bar stays fixed */}
      <nav
        className="sidebar-nav flex-1 overflow-y-auto pl-2 pr-2.5 pt-3 space-y-0.5 pb-2"
        style={{ minHeight: 0 }}
      >
        {visibleNav.map((item) => {
          const Icon = item.icon;
          const active = isSectionActive(item);

          // ─── Single page or panel ───
          if (item.type === 'single' || item.type === 'panel') {
            if (collapsed) {
              return <div key={item.id}>{renderCollapsedItem(item, active)}</div>;
            }
            return (
              <NavLink
                key={item.id}
                to={item.to ?? ''}
                className={clsx(
                  'flex items-center gap-3 px-3 rounded-md border-l-2 text-sm cursor-pointer transition-colors duration-100 select-none',
                  py,
                  active
                    ? 'border-accent bg-accent-muted text-accent'
                    : 'border-transparent text-ink-secondary hover:bg-surface-4 hover:text-ink-primary'
                )}
              >
                <Icon size={ICON_SIZE} className="flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.badge && item.to && (
                  <CountBadge
                    count={badgeCounts[item.to] || 0}
                    pulse={!!pulsePaths[item.to]}
                    onAnimationEnd={() =>
                      setPulsePaths((p) => {
                        const n = { ...p };
                        if (item.to) delete n[item.to];
                        return n;
                      })
                    }
                  />
                )}
              </NavLink>
            );
          }

          // ─── Expand section ───
          const isOpen = isSectionOpen(item);

          if (collapsed) {
            return <div key={item.id}>{renderCollapsedItem(item, active)}</div>;
          }

          return (
            <div key={item.id}>
              {/* Section header: left label + right chevron */}
              <div
                className={clsx(
                  'flex items-center rounded-md text-sm transition-colors duration-100 select-none',
                  active
                    ? 'bg-surface-3 text-ink-bright'
                    : 'text-ink-secondary hover:bg-surface-4 hover:text-ink-primary'
                )}
              >
                {/* Left 3/4: label area — navigates only */}
                <button
                  onClick={() => handleLabelClick(item)}
                  className={clsx('flex-1 flex items-center gap-3 px-3 min-w-0 cursor-pointer', py)}
                >
                  <Icon size={ICON_SIZE} className="flex-shrink-0" />
                  <span className="flex-1 truncate text-left">{item.label}</span>
                </button>
                {/* Right 1/4: chevron — toggle only, invisible zone.
                    min-w + min-h together meet the 44×44 WCAG hit target at
                    both densities; py-1.5 (compact) alone falls below 44px. */}
                <button
                  onClick={() => handleChevronClick(item)}
                  className={clsx(
                    'min-w-[48px] min-h-[44px] flex items-center justify-center flex-shrink-0 cursor-pointer',
                    'text-ink-muted hover:text-ink-primary transition-colors',
                    py
                  )}
                  aria-label={`Toggle ${item.label} submenu`}
                >
                  <ChevronDown
                    size={12}
                    className={clsx(
                      'transition-transform duration-200',
                      isOpen ? 'rotate-0' : '-rotate-90'
                    )}
                  />
                </button>
              </div>

              {/* Sub-items — filtered per role so dist_rep on Purchasing sees
                  only Receiving + Discrepancies, etc. */}
              {isOpen && item.children && (
                <div className="ml-5 mt-0.5 space-y-0.5 border-l border-border-subtle pl-2 pb-1">
                  {item.children
                    .filter((child) => canAccess(role, child.to))
                    .map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={clsx(
                          'flex items-center gap-2 px-2 py-1.5 rounded-md border-l-2 text-xs cursor-pointer transition-colors duration-100 select-none',
                          isPathActive(child.to)
                            ? 'border-accent bg-accent-muted text-accent font-medium'
                            : 'border-transparent text-ink-muted hover:bg-surface-4 hover:text-ink-secondary'
                        )}
                      >
                        <span className="flex-1 truncate">{child.label}</span>
                        {child.badge && (
                          <CountBadge
                            count={badgeCounts[child.to] || 0}
                            pulse={!!pulsePaths[child.to]}
                            onAnimationEnd={() =>
                              setPulsePaths((p) => {
                                const n = { ...p };
                                delete n[child.to];
                                return n;
                              })
                            }
                          />
                        )}
                      </NavLink>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom fixed bar — User | Help | Log out (never scrolls) */}
      <div className="px-2 py-2 flex-shrink-0">
        {collapsed ? (
          <div className="flex flex-col items-center gap-0.5">
            <Tooltip text={user?.email ?? 'User'}>
              <button
                onClick={() => navigate('/settings#profile')}
                className="w-full flex items-center justify-center h-9 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
                aria-label="User"
              >
                <User size={ICON_SIZE} />
              </button>
            </Tooltip>
            <Tooltip text="Help">
              <button
                ref={helpButtonRef}
                onClick={() => openHelp()}
                className="w-full flex items-center justify-center h-9 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
                aria-label="Help"
              >
                <HelpCircle size={ICON_SIZE} />
              </button>
            </Tooltip>
            <Tooltip text="Log out">
              <button
                onClick={signOut}
                className="w-full flex items-center justify-center h-9 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
                aria-label="Log out"
              >
                <LogOut size={ICON_SIZE} />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => navigate('/settings#profile')}
              className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer min-w-0"
              title={user?.email ?? 'User'}
              aria-label="User"
            >
              <User size={ICON_SIZE - 2} />
              <span className="text-2xs truncate w-full text-center">User</span>
            </button>
            <button
              ref={helpButtonRef}
              onClick={() => openHelp()}
              className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer min-w-0"
              title="Help"
              aria-label="Help"
            >
              <HelpCircle size={ICON_SIZE - 2} />
              <span className="text-2xs text-center">Help</span>
            </button>
            <button
              onClick={signOut}
              className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer min-w-0"
              title="Log out"
            >
              <LogOut size={ICON_SIZE - 2} />
              <span className="text-2xs text-center">Log out</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
